import { Upload } from 'lucide-react';
import type { ComponentType } from 'react';
import {
  siAlgolia,
  siEbay,
  siGooglegemini,
  siPerplexity,
  siPinterest,
  siTiktok,
} from 'simple-icons';

import { AdobeAnalyticsLogo } from './AdobeAnalyticsLogo';
import { AmazonLogo } from './AmazonLogo';
import { GoogleAdsLogo } from './GoogleAdsLogo';
import { GoogleAnalyticsLogo } from './GoogleAnalyticsLogo';
import { GoogleLogo } from './GoogleLogo';
import { MetaLogo } from './MetaLogo';
import { MicrosoftLogo } from './MicrosoftLogo';
import { OpenAiLogo } from './OpenAiLogo';
import { SalesforceLogo } from './SalesforceLogo';
import { ShopifyLogo } from './ShopifyLogo';
import { makeSimpleIconLogo } from './SimpleIconLogo';
import { WalmartLogo } from './WalmartLogo';
import { YandexLogo } from './YandexLogo';

// Brand marks bundled from `simple-icons` for connectors the backend doesn't
// return an `iconUrl` for. (Akeneo has no public single-mark source, so it
// still falls back to the initials tile.)
const AlgoliaLogo = makeSimpleIconLogo(siAlgolia);
const GeminiLogo = makeSimpleIconLogo(siGooglegemini);
const PerplexityLogo = makeSimpleIconLogo(siPerplexity);
const PinterestLogo = makeSimpleIconLogo(siPinterest);
const TikTokLogo = makeSimpleIconLogo(siTiktok);
const EbayLogo = makeSimpleIconLogo(siEbay);

/**
 * Bundled brand logos keyed by connection/connector `type`. Single source of
 * truth (connection lists, pickers, …) so a recognisable channel mark shows
 * even when the backend returns no `iconUrl`.
 */
export const CONNECTOR_LOGOS: Record<string, ComponentType<{ className?: string }>> = {
  GOOGLE_MERCHANT: GoogleLogo,
  GOOGLE_ADS: GoogleAdsLogo,
  GA4_ANALYTICS: GoogleAnalyticsLogo,
  GA4_REPORTING: GoogleAnalyticsLogo,
  GOOGLE_SEARCH_CONSOLE: GoogleLogo,
  GOOGLE_INDEXING: GoogleLogo,
  SEARCH_ADS_360: GoogleAdsLogo,
  META_COMMERCE: MetaLogo,
  META_ADS: MetaLogo,
  SHOPIFY: ShopifyLogo,
  BING_WEBMASTER: MicrosoftLogo,
  BING_INDEXNOW: MicrosoftLogo,
  MICROSOFT_MERCHANT: MicrosoftLogo,
  ADOBE_ANALYTICS: AdobeAnalyticsLogo,
  // Brand marks from simple-icons.
  ALGOLIA: AlgoliaLogo,
  GEMINI: GeminiLogo,
  PERPLEXITY: PerplexityLogo,
  PINTEREST_SHOP: PinterestLogo,
  TIKTOK_SHOP: TikTokLogo,
  TIKTOK_ADS: TikTokLogo,
  EBAY: EbayLogo,
  // Hand-authored brand marks (sourced from official SVGs).
  AMAZON: AmazonLogo,
  SALESFORCE_COMMERCE: SalesforceLogo,
  OPENAI: OpenAiLogo,
  YANDEX_INDEXNOW: YandexLogo,
  WALMART: WalmartLogo,
  // Manual Upload is not a brand — use a neutral upload glyph so it still reads
  // as an icon rather than an initials tile.
  MANUAL_UPLOAD: Upload,
};
